function fixContentApiUrlInEditMode(url: string): string {
  const pattern = /(api\/episerver\/v2\.0\/content\/)(\d+)_\d+/;
  if (pattern.test(url)) {
    return url.replace(pattern, '$1$2');
  }
  return url;
}
import Axios, { AxiosRequestConfig, Method, AxiosResponse } from 'axios';
import AppConfig from './AppConfig';
import IContent from './Models/IContent';
import ContentLink, { ContentReference, ContentLinkService } from './Models/ContentLink';
import ActionResponse, { ResponseType } from './Models/ActionResponse';
import WebsiteList from './Models/WebsiteList';
import Website from './Models/Website';
import PathProvider from './PathProvider';
import Property from './Property';
import { getWebsite } from './websites';

export type PathResponse = IContent | ActionResponse<any>;

export interface NetworkErrorData extends IContent {
  error: Property<any>;
}

export function PathResponseIsIContent(iContent: PathResponse): iContent is IContent {
  if ((iContent as ActionResponse<any>).actionName) {
    return false;
  }
  return true;
}

export default class ContentDeliveryAPI {
  protected config: AppConfig;
  protected componentService: string = '/api/episerver/v2.0/content/';
  protected websiteService: string = '/api/episerver/v3/site/';
  protected methodService: string = '/api/episerver/v3/action/';
  protected debug: boolean = false;
  protected pathProvider: PathProvider;

  //Rosti change
  protected authenticationProvider: any = null;

  /**
   * Marker to keep if we're in edit mode
   */
  protected inEditMode: boolean = false;

  /**
   * Internal cache of the websites retrieved from the ContentDelivery API
   *
   * @private
   */
  private websites!: WebsiteList;

  /**
   * Internal cache of the current website, as retrieved from the ContentDelivery API
   *
   * @private
   */
  private website!: Website;

  //Rosti change
  constructor(pathProvider: PathProvider, config: AppConfig, authenticationProvider: any) {
    this.pathProvider = pathProvider;
    this.config = config;
    this.debug = this.config.enableDebug === true;
    //Rosti change
    this.authenticationProvider = authenticationProvider;
  }

  public get currentPathProvider(): PathProvider {
    return this.pathProvider;
  }

  public get currentConfig(): AppConfig {
    return this.config;
  }

  public isInEditMode(): boolean {
    return this.inEditMode;
  }

  public setInEditMode(editMode: boolean): ContentDeliveryAPI {
    this.inEditMode = editMode === true;
    return this;
  }

  public isDisabled(): boolean {
    return this.config.noAjax === true;
  }

  /**
   * Invoke an ASP.Net MVC controller method using the generic content API. This is intended
   * to be used only when attaching a SPA to an existing code-base.
   *
   * @param content The content for which the controller must be loaded
   * @param method  The (case sensitive) method name to invoke on the controller
   * @param verb    The HTTP verb to use when invoking the controller
   * @param data    The data (if any) to send to the controller for the method
   */
  public async invokeControllerMethod(
    content: ContentLink,
    method: string,
    verb?: Method,
    data?: object
  ): Promise<any> {
    let options = this.getRequestSettings(verb);
    options.data = data;
    return await this.doRequest<any>(this.getMethodServiceUrl(content, method), options);
  }

  /**
   * Strongly typed variant of invokeControllerMethod
   *
   * @see   invokeControllerMethod()
   * @param content The content for which the controller must be loaded
   * @param method  The (case sensitive) method name to invoke on the controller
   * @param verb    The HTTP verb to use when invoking the controller
   * @param data    The data (if any) to send to the controller for the method
   */
  public async invokeTypedControllerMethod<TypeOut, TypeIn>(
    content: ContentLink,
    method: string,
    verb?: Method,
    data?: TypeIn
  ): Promise<ActionResponse<TypeOut>> {
    let options = this.getRequestSettings(verb);
    options.data = data;
    return await this.doRequest<ActionResponse<TypeOut>>(this.getMethodServiceUrl(content, method), options);
  }

  /**
   * Retrieve a list of all websites registered within Episerver
   */
  public async getWebsites(): Promise<WebsiteList> {
    if (!this.websites) {
      const options = this.getRequestSettings();
      options.withCredentials = false;
      //this.websites = await this.doRequest<WebsiteList>(this.config.epiBaseUrl + this.websiteService, options);
      this.websites = getWebsite(this.config.staticWebSiteUrl, this.config.startPageBlock, this.config.guidValue);
    } else {
      this.websites[0].contentRoots.startPage = {
        ...this.websites[0].contentRoots.startPage,
        id: parseInt(this.config.startPageBlock || '914', 10),
      }; //always override start page with block id from config
    }
    console.log('[website: ]', this.websites);
    return this.websites;
  }

  /**
   * Retrieve the first website registered within Episerver
   */
  public async getWebsite(): Promise<Website> {
    const list = await this.getWebsites();
    return list[0];
  }

  public async getContent(content: ContentLink, forceGuid: boolean = false): Promise<IContent | null> {
    if (!(content && (content.guidValue || content.url))) {
      if (this.config.enableDebug) {
        console.warn('Loading content for an empty reference ', content);
      }
      return null;
    }
    let useGuid = content.guidValue ? this.config.preferGuid || forceGuid : false;
    let serviceUrl: URL;
    if (useGuid) {
      serviceUrl = new URL(this.config.epiBaseUrl + this.componentService + content.guidValue);
    } else {
      try {
        serviceUrl = new URL(
          this.config.epiBaseUrl +
            (content.url ? content.url : this.componentService + ContentLinkService.createApiId(content))
        );
      } catch (e) {
        serviceUrl = new URL(this.config.epiBaseUrl + this.componentService + ContentLinkService.createApiId(content));
      }
    }
    serviceUrl.searchParams.append('currentPageUrl', this.pathProvider.getCurrentPath());
    if (this.config.autoExpandRequests) {
      serviceUrl.searchParams.append('expand', '*');
    }
    return this.doRequest<IContent>(serviceUrl.href).catch((r) => {
      return this.buildNetworkError(r);
    });
  }

  public async getContentsByRefs(refs: Array<string>): Promise<Array<IContent>> {
    if (!refs || refs.length == 0) {
      return Promise.resolve<Array<IContent>>([]);
    }
    let serviceUrl: URL = new URL(this.config.epiBaseUrl + this.componentService);
    serviceUrl.searchParams.append('references', refs.join(','));
    if (this.config.autoExpandRequests) {
      serviceUrl.searchParams.append('expand', '*');
    }
    return this.doRequest<Array<IContent>>(serviceUrl.href).catch((r) => {
      return [];
    });
  }

  public async getContentByRef(ref: string, shouldCache?: boolean): Promise<IContent> {
    let serviceUrl: URL = new URL(this.config.epiBaseUrl + this.componentService + ref);
    if (this.config.autoExpandRequests) {
      serviceUrl.searchParams.append('expand', '*');
    }
    const options = this.getRequestSettings();
    options.withCredentials = !shouldCache;
    return this.doRequest<IContent>(serviceUrl.href, options).catch((r) => {
      return this.buildNetworkError(r);
    });
  }

  public async getContentByPath(path: string): Promise<PathResponse> {
    let serviceUrl: URL = new URL(this.config.epiBaseUrl + path);
    if (this.config.autoExpandRequests) {
      serviceUrl.searchParams.append('expand', '*');
    }
    serviceUrl.searchParams.append('currentPageUrl', this.pathProvider.getCurrentPath());
    return this.doRequest<PathResponse>(serviceUrl.href).catch((r) => {
      return this.buildNetworkError(r, path);
    });
  }

  public async getContentChildren<T extends IContent>(id: ContentReference): Promise<Array<T>> {
    let itemId: string = ContentLinkService.createApiId(id);
    let serviceUrl: URL = new URL(this.config.epiBaseUrl + this.componentService + itemId + '/children');
    if (this.config.autoExpandRequests) {
      serviceUrl.searchParams.append('expand', '*');
    }
    return this.doRequest<Array<T>>(serviceUrl.href).catch((r) => {
      return [];
    });
  }

  public async getContentAncestors(link: ContentReference): Promise<Array<IContent>> {
    let itemId: string = ContentLinkService.createApiId(link);
    let serviceUrl: URL = new URL(`${this.config.epiBaseUrl}${this.componentService}${itemId}/ancestors`);
    if (this.config.autoExpandRequests) {
      serviceUrl.searchParams.append('expand', '*');
    }
    return this.doRequest<Array<IContent>>(serviceUrl.href).catch((r) => {
      return [];
    });
  }

  /**
   * Perform the actual request
   *
   * @param url The URL to request the data from
   * @param options The Request options to use
   */
  protected async doRequest<T>(url: string, options?: AxiosRequestConfig): Promise<T> {
    if (this.isDisabled()) {
      console.warn(`[doRequest] API is disabled. Skipping request to ${url}`);
      return Promise.reject('The Content Delivery API has been disabled');
    }

    url = fixContentApiUrlInEditMode(url);
    let isPreloadApi;

    isPreloadApi = url.includes(`/${this.config.startPageBlock}?expand`);

    if (this.isInEditMode()) {
      const urlObj = new URL(url);
      urlObj.searchParams.append('epieditmode', 'True');
      urlObj.searchParams.append('preventCache', Math.round(Math.random() * 100000000).toString());
      url = urlObj.href;
    }

    options = options ?? this.getRequestSettings();
    options.url = url;

    console.debug(`[doRequest] Requesting: ${url}`);

    if (isPreloadApi && typeof window !== 'undefined') {
      const preloadFn = (window as any).__startPagePreload;
      if (typeof preloadFn === 'function') {
        console.debug(`[doRequest] Found preload function for ${url}`);
        try {
          const preloadData = await preloadFn();
          console.debug(`[doRequest] Using preload data for ${url}`);
          return preloadData as T;
        } catch (err) {
          console.warn(`[doRequest] Preload failed or timed out for ${url}`, err);
          // Proceed to fetch normally
        }
      } else {
        console.debug(`[doRequest] No preload function found for ${url}`);
      }
    }

    console.debug(`[doRequest] Final headers [${url}] before request:`, JSON.stringify(options.headers, null, 2));

    // Proceed with actual request
    return Axios.request<any, AxiosResponse<T>>(options)
      .then((response) => {
        //console.debug(`[doRequest] Response from ${url}:`, response.data);
        return response.data;
      })
      .catch((reason) => {
        console.error(`[doRequest] HTTP Fetch error from ${url}:`, reason);
        throw reason;
      });
  }

  protected getMethodServiceUrl(content: ContentLink, method: string): string {
    let contentUrl: string = this.config.epiBaseUrl;
    contentUrl = contentUrl + this.methodService;
    contentUrl = contentUrl + content.guidValue + '/' + method;
    return contentUrl;
  }

  /**
   * Build the request parameters needed to perform the call to the Content Delivery API
   *
   * @param verb The verb for the generated configuration
   */
  protected getRequestSettings(verb?: Method): AxiosRequestConfig {
    const headers = { ...this.getHeaders() };
    let options: AxiosRequestConfig = {
      method: verb ? verb : 'get',
      baseURL: this.config.epiBaseUrl,
      withCredentials: true,
      headers,
      transformRequest: [
        (data: any, headers: any) => {
          if (data) {
            headers['Content-Type'] = 'application/json';
            return JSON.stringify(data);
          }
          return data;
        },
      ],
      responseType: 'json',
    };
    return options;
  }

  protected getHeaders(customHeaders?: object): object {
    let defaultHeaders: any = {
      Accept: 'application/json',
      'Accept-Language': this.config.defaultLanguage || '', //@ToDo: Convert to context call, with default
    };
    //Rosti change
    if (this.authenticationProvider?.getSavedToken())
      defaultHeaders['Authorization'] = 'Bearer ' + this.authenticationProvider?.getSavedToken();

    if (!customHeaders) return defaultHeaders;

    return {
      ...defaultHeaders,
      ...customHeaders,
    };
  }

  public static IsActionResponse(response: PathResponse): response is ActionResponse<any> {
    if (
      response &&
      (response as ActionResponse<any>).responseType &&
      (response as ActionResponse<any>).responseType == ResponseType.ActionResult
    ) {
      return true;
    }
    return false;
  }

  private counter: number = 0;

  protected buildNetworkError(reason: any, path: string = ''): NetworkErrorData {
    const errorId = ++this.counter;
    return {
      name: {
        propertyDataType: 'String',
        value: 'Error',
      },
      contentType: ['Errors', 'NetworkError'],
      contentLink: {
        guidValue: '',
        id: errorId,
        providerName: 'ContentDeliveryAPI_Errors',
        url: path,
        workId: 0,
      },
      error: {
        propertyDataType: 'Unknown',
        value: JSON.stringify(reason), //Rosti - changed to support auhtenticated middleware
      },
    };
  }
}
